// Admin Service - API abstraction for admin operations
// Handles authentication and product management

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:3001';

// Session timeout: 2 hours
const SESSION_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

// Auth token management
export const authService = {
  // Store token with timestamp
  setToken(token: string): void {
    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_token_time', Date.now().toString());
  },

  // Get token (returns null if expired)
  getToken(): string | null {
    const token = localStorage.getItem('admin_token');
    const tokenTime = localStorage.getItem('admin_token_time');
    
    if (!token || !tokenTime) {
      return null;
    }
    
    // Check if session expired
    const elapsed = Date.now() - parseInt(tokenTime, 10);
    if (elapsed > SESSION_TIMEOUT) {
      console.log('Session expired');
      this.clearToken();
      return null;
    }
    
    return token;
  },

  // Remove token
  clearToken(): void {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_token_time');
  },

  // Check if authenticated (and not expired)
  isAuthenticated(): boolean {
    return !!this.getToken();
  },
  
  // Get remaining session time in seconds
  getRemainingSessionTime(): number {
    const tokenTime = localStorage.getItem('admin_token_time');
    if (!tokenTime) return 0;
    
    const elapsed = Date.now() - parseInt(tokenTime, 10);
    const remaining = Math.max(0, SESSION_TIMEOUT - elapsed);
    return Math.floor(remaining / 1000);
  },

  // Login
  async login(password: string): Promise<{ token: string; expiresIn: number }> {
    const response = await fetch(`${API_ENDPOINT}/admin/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    this.setToken(data.token);
    return data;
  },

  // Verify token
  async verify(): Promise<boolean> {
    const token = this.getToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_ENDPOINT}/admin/auth/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return response.ok;
    } catch (error) {
      return false;
    }
  },

  // Logout
  logout(): void {
    this.clearToken();
  }
};

// Product management
export const adminProductService = {
  // Get auth headers
  getHeaders(): HeadersInit {
    const token = authService.getToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  },

  // List all products
  async getProducts(): Promise<any[]> {
    const response = await fetch(`${API_ENDPOINT}/admin/products`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch products');
    }

    const data = await response.json();
    return data.products;
  },

  // Create product
  async createProduct(product: any): Promise<any> {
    const response = await fetch(`${API_ENDPOINT}/admin/products`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(product)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create product');
    }

    const data = await response.json();
    return data.product;
  },

  // Update product
  async updateProduct(productId: string, updates: any): Promise<any> {
    const response = await fetch(`${API_ENDPOINT}/admin/products/${productId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update product');
    }

    const data = await response.json();
    return data.product;
  },

  // Delete product (soft delete)
  async deleteProduct(productId: string): Promise<void> {
    const response = await fetch(`${API_ENDPOINT}/admin/products/${productId}`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to delete product');
    }
  },

  // Upload image to S3
  async uploadImage(file: File, productName?: string): Promise<string> {
    // Step 1: Get presigned URL
    const response = await fetch(`${API_ENDPOINT}/admin/upload`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type,
        productName: productName
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get upload URL');
    }

    const { uploadUrl, publicUrl, key } = await response.json();

    // Step 2: Upload file to S3 using presigned URL
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type
      },
      body: file
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload image to S3');
    }

    // Return both public URL and S3 key
    return JSON.stringify({ publicUrl, s3Key: key });
  },

  // Delete image from S3
  async deleteImage(imageUrl: string): Promise<void> {
    const response = await fetch(`${API_ENDPOINT}/admin/upload`, {
      method: 'DELETE',
      headers: this.getHeaders(),
      body: JSON.stringify({ imageUrl })
    });

    if (!response.ok) {
      throw new Error('Failed to delete image');
    }
  },

  // Get categories
  async getCategories(): Promise<string[]> {
    const response = await fetch(`${API_ENDPOINT}/admin/categories`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }

    const data = await response.json();
    return data.categories;
  },

  // Get tags
  async getTags(): Promise<string[]> {
    const response = await fetch(`${API_ENDPOINT}/admin/tags`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch tags');
    }

    const data = await response.json();
    return data.tags;
  }
};